import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type FormEvent } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalAlert, MinimalBadge, MinimalButton, MinimalDropdown, MinimalInput } from '@ovasabi/ui-minimal'
import { subscribePrototypeRuntime } from '../../stores/activeRuntime'
import { CheckIcon } from '../../components/icons'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'

export interface PayoutAccountData {
  bank_code: string
  bank_name: string
  account_tail: string
  account_name?: string
  status: string
  account_mismatch: boolean
  bank_confirmed: boolean
  payout_ready: boolean
}

export const NIGERIAN_BANKS = [
  { code: '044', name: 'Access Bank' },
  { code: '023', name: 'Citibank Nigeria' },
  { code: '050', name: 'Ecobank Nigeria' },
  { code: '070', name: 'Fidelity Bank' },
  { code: '011', name: 'First Bank of Nigeria' },
  { code: '214', name: 'First City Monument Bank (FCMB)' },
  { code: '058', name: 'Guaranty Trust Bank (GTBank)' },
  { code: '030', name: 'Heritage Bank' },
  { code: '301', name: 'Jaiz Bank' },
  { code: '082', name: 'Keystone Bank' },
  { code: '50211', name: 'Kuda Bank' },
  { code: '50515', name: 'Moniepoint Microfinance Bank' },
  { code: '999992', name: 'OPay' },
  { code: '999991', name: 'PalmPay' },
  { code: '076', name: 'Polaris Bank' },
  { code: '101', name: 'Providus Bank' },
  { code: '221', name: 'Stanbic IBTC Bank' },
  { code: '068', name: 'Standard Chartered Bank' },
  { code: '232', name: 'Sterling Bank' },
  { code: '100', name: 'Suntrust Bank' },
  { code: '032', name: 'Union Bank of Nigeria' },
  { code: '033', name: 'United Bank for Africa (UBA)' },
  { code: '215', name: 'Unity Bank' },
  { code: '035', name: 'Wema Bank' },
  { code: '057', name: 'Zenith Bank' },
] as const

const readAccount = (payload: Record<string, unknown>): PayoutAccountData | null => {
  if (!payload.account_tail) return null
  if (!/^\d{4}$/.test(String(payload.account_tail)) || typeof payload.bank_confirmed !== 'boolean' ||
    typeof payload.payout_ready !== 'boolean' || typeof payload.account_mismatch !== 'boolean' || !payload.status) {
    throw new Error('Bank account status could not be verified. Reload account details.')
  }
  return {
    bank_code: String(payload.bank_code ?? ''), bank_name: String(payload.bank_name || NIGERIAN_BANKS.find(bank => bank.code === String(payload.bank_code))?.name || 'Nigerian Bank'),
    account_tail: String(payload.account_tail), status: String(payload.status),
    account_mismatch: payload.account_mismatch, bank_confirmed: payload.bank_confirmed,
    payout_ready: payload.payout_ready && payload.bank_confirmed && !payload.account_mismatch && payload.status === 'active',
  }
}

const STORAGE_PREFIX = 'chow_chef_payout_account:'

export const useChefPayoutAccount = (profileId: string) => {
  const live = useSyncExternalStore(subscribePrototypeRuntime, isLiveRuntime, () => false)
  const [account, setAccount] = useState<PayoutAccountData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')

  const requestVersion = useRef(0)
  const savingRef = useRef(false)

  const load = useCallback(async () => {
    const version = ++requestVersion.current
    setAccount(null)
    setNotice('')
    setSaving(false)
    setLoading(true)
    setError('')
    try {
      if (live) {
        const payload = commandPayload(await commands.verification.getMyPayoutAccount())
        if (version === requestVersion.current) setAccount(readAccount(payload))
      } else {
        const stored = sessionStorage.getItem(`${STORAGE_PREFIX}${profileId}`)
        if (version === requestVersion.current) setAccount(stored ? readAccount(JSON.parse(stored)) : null)
      }
    } catch (err) {
      if (version !== requestVersion.current) return
      const message = err instanceof Error ? err.message : 'Could not load bank account details.'
      if (!/payout_account_missing|no payout account has been submitted/i.test(message)) setError(message)
    } finally {
      if (version === requestVersion.current) setLoading(false)
    }
  }, [profileId, live])

  useEffect(() => {
    void load()
    const versionRef = requestVersion
    return () => { versionRef.current++ }
  }, [load])

  const savePayoutAccount = async (input: { bankCode: string; accountNumber: string; accountName: string }) => {
    if (savingRef.current) throw new Error('Bank account save is already in progress.')
    if (!/^\d{10}$/.test(input.accountNumber) || input.accountName.trim().length < 2 || !/^\d{1,8}$/.test(input.bankCode)) {
      throw new Error('Enter a bank, a 10-digit account number, and your account name.')
    }
    savingRef.current = true
    const version = ++requestVersion.current
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const bank = NIGERIAN_BANKS.find((b) => b.code === input.bankCode)
      const saved = isLiveRuntime()
        ? readAccount(commandPayload(await commands.verification.submitPayoutAccount({
          bank_code: input.bankCode, account_number: input.accountNumber, account_name: input.accountName.trim(),
        })))
        : readAccount({ bank_code: input.bankCode, bank_name: bank?.name ?? 'Nigerian Bank',
          account_tail: input.accountNumber.slice(-4), status: 'pending', account_mismatch: false,
          bank_confirmed: false, payout_ready: false })
      if (!saved) throw new Error('We couldn’t confirm your account was saved. Reload the page, then try again.')
      if (version === requestVersion.current) {
        setAccount(saved)
        if (!isLiveRuntime()) sessionStorage.setItem(`${STORAGE_PREFIX}${profileId}`, JSON.stringify(saved))
        setNotice(saved.account_mismatch ? 'Account saved. We need to check the bank name before we can pay you.' : 'Account saved. You’ll see below when it’s ready for payouts.')
      }
      return saved
    } catch (err) {
      if (version === requestVersion.current) {
        setAccount(null)
        setError(err instanceof Error ? err.message : 'Could not save bank account. Please check details.')
      }
      throw err
    } finally {
      savingRef.current = false
      if (version === requestVersion.current) setSaving(false)
    }
  }

  return { account, loading, error, notice, saving, savePayoutAccount, reload: load }
}

const Style = {
  Card: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
  `,
  Row: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    flex-wrap: wrap;
  `,
  Title: styled.h3`
    margin: 0;
    font-size: ${minimalVars.typography.bodySize};
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
  `,
  Sub: styled.p`
    margin: 0;
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textSecondary};
    line-height: 1.4;
  `,
  Meta: styled.span`
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textTertiary};
  `,
  Form: styled.form`
    display: grid;
    gap: 12px;
  `,
  AccountSummary: styled.div`
    display: grid;
    gap: 8px;
    padding: 12px;
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
  `,
  Badges: styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  `,
}

export interface ChefPayoutAccountFormProps {
  profileId: string
  compact?: boolean
  onSuccess?: () => void
}

export const ChefPayoutAccountForm = ({ profileId, compact = false, onSuccess }: ChefPayoutAccountFormProps) => {
  const { account, loading, error, notice, saving, savePayoutAccount, reload } = useChefPayoutAccount(profileId)
  const [editing, setEditing] = useState(false)
  const [bankCode, setBankCode] = useState(account?.bank_code || '058')
  const [accountNumber, setAccountNumber] = useState('')
  const [accountName, setAccountName] = useState(account?.account_name || '')

  useEffect(() => {
    if (account?.bank_code) setBankCode(account.bank_code)
    if (account?.account_name) setAccountName(account.account_name)
  }, [account])

  useEffect(() => {
    setEditing(false)
    setAccountNumber('')
    setAccountName('')
  }, [profileId])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!bankCode || accountNumber.trim().length !== 10 || !accountName.trim()) {
      return
    }
    try {
      await savePayoutAccount({
        bankCode,
        accountNumber: accountNumber.trim(),
        accountName: accountName.trim(),
      })
      setEditing(false)
      onSuccess?.()
    } catch {
      /* error is set in hook */
    }
  }

  if (loading) {
    return <Style.Sub>Loading account details…</Style.Sub>
  }

  const showForm = editing || !account

  return (
    <Style.Card>
      {notice && <MinimalAlert tone="success" title="Saved">{notice}</MinimalAlert>}
      {error && <MinimalAlert tone="danger" title="Account status unavailable">{error}<MinimalButton size="sm" variant="ghost" onClick={() => void reload()}>Reload account details</MinimalButton></MinimalAlert>}

      {!showForm && account ? (
        <Style.AccountSummary>
          <Style.Row>
            <div>
              <Style.Title>{account.bank_name}</Style.Title>
              <Style.Sub>Account ending in ••••{account.account_tail}</Style.Sub>
              {account.account_name && <Style.Meta>{account.account_name}</Style.Meta>}
            </div>
            <MinimalButton size="sm" variant="ghost" disabled={account.payout_ready || saving} onClick={() => setEditing(true)}>
              Edit
            </MinimalButton>
          </Style.Row>
          <Style.Badges>
            {account.bank_confirmed ? (
              <MinimalBadge tone="success" size="sm">
                <CheckIcon size="sm" /> Bank verified
              </MinimalBadge>
            ) : (
              <MinimalBadge tone="info" size="sm">In review</MinimalBadge>
            )}
            {account.payout_ready && (
              <MinimalBadge tone="success" size="sm">Payout ready</MinimalBadge>
            )}
            {account.payout_ready && <Style.Sub>To change the account we pay you into, contact support.</Style.Sub>}
            {account.account_mismatch && (
              <MinimalBadge tone="warning" size="sm">Name mismatch</MinimalBadge>
            )}
          </Style.Badges>
        </Style.AccountSummary>
      ) : (
        <Style.Form onSubmit={(e) => void handleSubmit(e)}>
          {!compact && (
            <Style.Sub>
              Enter the Nigerian bank account in your legal name where customer payments and payouts will be sent via Paystack.
            </Style.Sub>
          )}
          <MinimalDropdown
            label="Select Bank"
            value={bankCode}
            onChange={setBankCode}
            disabled={saving}
            searchable
            searchPlaceholder="Search banks"
            options={NIGERIAN_BANKS.map((bank) => ({ value: bank.code, label: bank.name }))}
          />

          <MinimalInput
            label="NUBAN Account Number (10 digits)"
            placeholder="0123456789"
            inputMode="numeric"
            maxLength={10}
            minLength={10}
            required
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
            disabled={saving}
          />

          <MinimalInput
            label="Account Name (as registered with your bank)"
            placeholder="e.g. Adaeze Okafor"
            maxLength={120}
            required
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            disabled={saving}
          />

          <Style.Row>
            {account && (
              <MinimalButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </MinimalButton>
            )}
            <MinimalButton
              type="submit"
              size="sm"
              loading={saving}
              disabled={saving || !bankCode || accountNumber.length !== 10 || accountName.trim().length < 2}
            >
              {account ? 'Update bank account' : 'Save bank account'}
            </MinimalButton>
          </Style.Row>
        </Style.Form>
      )}
    </Style.Card>
  )
}
